import React, { useEffect, useState } from 'react';
import { GlobalOutlined, UserOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Flex,
  Form,
  Input,
  Modal,
  Radio,
  RadioGroupProps,
  Select,
  Space,
  Typography,
} from 'antd';
import { useUser } from '@hooks/useUser';
import { CANCEL_BUTTON_LABEL, SAVE_BUTTON_LABEL } from '@constants/common';
import { StatusCodes } from '@constants/enum/common';
import { EventControlApiKeysElement } from '@constants/event';
import {
  API_KEY_TYPE_DESCRIPTIONS,
  API_KEY_TYPE_LABELS,
  ApiKeyType,
  EDIT_API_KEY_MODAL,
  OPTIONAL_FIELD_MARK,
  USER_ROLE_OPTIONS,
} from '@modules/APIKeys/constants/apiKeys';
import useApiKeys from '@modules/APIKeys/hooks/useApiKeys';
import { ApiKeyEventPayload, IApiKey, IUpdateApiKeyPayload } from '@modules/APIKeys/types/apiKeys';
import styles from './apiKeyEditForm.module.scss';

interface Props {
  apiKey: IApiKey;
  isOpen: boolean;
  loading?: boolean;
  onClose: () => void;
}

const { Compact } = Space;
const { Item, useForm } = Form;
const { Text } = Typography;

const ApiKeyEditForm: React.FC<Props> = ({ apiKey, isOpen, loading, onClose }) => {
  const [form] = useForm<IUpdateApiKeyPayload>();

  const {
    user: { role: userRole },
    isOrganizationAdmin,
  } = useUser();
  const { updateApiKey, updateApiKeyStatus, trackApiKeyUserEvent } = useApiKeys();

  const [descriptionText, setDescriptionText] = useState<string>('');

  const personalOptions: RadioGroupProps['options'] = [
    {
      label: (
        <Space size={4}>
          <UserOutlined /> {API_KEY_TYPE_LABELS[ApiKeyType.Personal]}
        </Space>
      ),
      value: true,
    },
    {
      label: (
        <Space size={4}>
          <GlobalOutlined /> {API_KEY_TYPE_LABELS[ApiKeyType.Service]}
        </Space>
      ),
      value: false,
    },
  ];

  const onFormValuesChange = ({ personal }: IUpdateApiKeyPayload) => {
    setDescriptionText(
      personal
        ? API_KEY_TYPE_DESCRIPTIONS[ApiKeyType.Personal]
        : API_KEY_TYPE_DESCRIPTIONS[ApiKeyType.Service],
    );
  };

  const onFinish = async (payload: IUpdateApiKeyPayload) => {
    try {
      const { api_token_id: apiKeyId } = apiKey;
      const userEvent: ApiKeyEventPayload = {
        event: EventControlApiKeysElement.SAVE_KEY,
        properties: {
          api_token_id: apiKeyId,
        },
      };

      await updateApiKey(apiKeyId, payload);
      await trackApiKeyUserEvent(userEvent);
    } finally {
      form.resetFields();
    }
  };

  const renderFooter = () => {
    return (
      <Flex justify="end" gap={8} className={styles.footer}>
        <Button onClick={onClose}>{CANCEL_BUTTON_LABEL}</Button>
        <Button type="primary" onClick={() => form.submit()} loading={loading}>
          {SAVE_BUTTON_LABEL}
        </Button>
      </Flex>
    );
  };

  useEffect(() => {
    onFormValuesChange(apiKey as IUpdateApiKeyPayload);
  }, [apiKey.personal]);

  useEffect(() => {
    if (updateApiKeyStatus === StatusCodes.SUCCESS) {
      onClose();
    }
  }, [updateApiKeyStatus]);

  return (
    <Modal
      title={EDIT_API_KEY_MODAL.TITLE}
      open={isOpen}
      onCancel={onClose}
      footer={renderFooter()}
      width={520}
      className={styles.modal}
    >
      {isOrganizationAdmin() ? (
        <Alert
          banner
          message={<Text strong>{EDIT_API_KEY_MODAL.INFO_TEXT}</Text>}
          type="info"
          showIcon
        />
      ) : null}
      <div className={styles.contentWrap}>
        <Form
          form={form}
          onFinish={onFinish}
          initialValues={{
            name: apiKey.name,
            personal: apiKey.personal,
            role: isOrganizationAdmin() ? apiKey.role : userRole,
          }}
          layout="vertical"
          validateTrigger={['onChange', 'onBlur']}
          onValuesChange={onFormValuesChange}
        >
          <Item name="personal">
            <Radio.Group options={personalOptions} optionType="button" />
          </Item>

          <Item>
            <Text>{descriptionText}</Text>
          </Item>

          <Item
            name="name"
            label={
              <Compact>
                {EDIT_API_KEY_MODAL.FORM_ITEMS.NAME.LABEL}
                <Text className={styles.optionalMark}>{OPTIONAL_FIELD_MARK}</Text>
              </Compact>
            }
          >
            <Input placeholder={EDIT_API_KEY_MODAL.FORM_ITEMS.NAME.PLACEHOLDER} />
          </Item>

          {isOrganizationAdmin() ? (
            <Item name="role" label={EDIT_API_KEY_MODAL.FORM_ITEMS.ROLE.LABEL}>
              <Select options={USER_ROLE_OPTIONS} />
            </Item>
          ) : null}
        </Form>
      </div>
    </Modal>
  );
};

export default ApiKeyEditForm;
